import {
  Bar,
  Checkbox,
  HintError,
  Input,
  Row,
  RowList,
  Stack,
  Switch,
  Typography,
} from '@src/components';

export type ExclusionOption = {
  /** Stable identity when participant names are not unique. */
  id?: string;
  name: string;
  /** This person is on the editor subject's exclusion list. */
  selected: boolean;
  /** ...and the subject is on theirs. */
  mutual: boolean;
};

export interface ExclusionEditorProps {
  options: ExclusionOption[];
  subtitle: string;
  /** Search only appears for a list long enough to need it. */
  query: string;
  onQueryChange: (query: string) => void;
  searchLabel: string;
  peopleLabel: string;
  mutualLabel: string;
  mutualHint: string;
  /** Set when the current selection makes a draw impossible. */
  error?: string;
  onToggle: (name: string, selected: boolean) => void;
  onToggleMutual: (name: string, mutual: boolean) => void;
  testId?: string;
}

/** A list of names long enough that scanning it beats scrolling it. */
const SEARCH_THRESHOLD = 12;

/**
 * The body of the per-participant exclusion dialog. It keeps no state of its
 * own: the page owns the draft and the search term, so re-showing the dialog
 * after every toggle renders the same thing it would have rendered anyway.
 */
export function ExclusionEditor({
  options,
  subtitle,
  query,
  onQueryChange,
  searchLabel,
  peopleLabel,
  mutualLabel,
  mutualHint,
  error,
  onToggle,
  onToggleMutual,
  testId,
}: ExclusionEditorProps) {
  const searchable = options.length >= SEARCH_THRESHOLD;
  const needle = query.trim().toLowerCase();
  const visible =
    searchable && needle
      ? options.filter((option) => option.name.toLowerCase().includes(needle))
      : options;
  const anySelected = options.some((option) => option.selected);

  return (
    <Stack gap={5} testId={testId}>
      <Stack gap={2}>
        <Typography as="p" role="body/sm">
          {subtitle}
        </Typography>
        {anySelected && (
          <Typography as="p" role="body/sm">
            {mutualHint}
          </Typography>
        )}
      </Stack>

      {searchable && (
        <Input
          type="search"
          name="exclusion-search"
          label={searchLabel}
          value={query}
          onValueChange={onQueryChange}
          autoComplete="off"
          testId="exclusion-search"
        />
      )}

      <Stack gap={3}>
        <Typography as="h3" role="label/field">
          {peopleLabel}
        </Typography>
        <RowList testId="exclusion-list">
          {visible.map((option) => (
            <Row
              key={option.id ?? option.name}
              align="center"
              testId={`exclusion-option-${option.id ?? option.name}`}
            >
              <Bar align="center" gap={4} wide>
                <Checkbox
                  fit
                  checked={option.selected}
                  onCheckedChange={(selected) => onToggle(option.id ?? option.name, selected)}
                  label={option.name}
                  testId={`exclusion-choice-${option.id ?? option.name}`}
                />
                {option.selected && (
                  <Switch
                    checked={option.mutual}
                    onCheckedChange={(mutual) => onToggleMutual(option.id ?? option.name, mutual)}
                    label={mutualLabel}
                    testId={`exclusion-mutual-${option.id ?? option.name}`}
                  />
                )}
              </Bar>
            </Row>
          ))}
        </RowList>
      </Stack>

      <HintError error={error ?? null} testId="exclusion-error" />
    </Stack>
  );
}
